import { useContext, useState } from 'react';
import { LayoutDashboard, AlertTriangle, Map, Settings, ChevronDown, ChevronRight, Eye, User, LogOut, X, Target, FileText, HelpCircle, History, CheckCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';

// ─── Section header with divider ────────────────────────────────────────────
function SectionHeader({ label }) {
  return (
    <div className="pt-3 pb-1">
      <p className="text-[10px] font-medium uppercase tracking-widest text-gray-400 px-1 mb-1.5">
        {label}
      </p>
      <div className="h-px bg-gray-100" />
    </div>
  );
}

// ─── Single nav link ─────────────────────────────────────────────────────────
function NavItem({ item, isActive, onClick }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
        isActive
          ? 'bg-green-50 text-green-700 border-l-4 border-green-600'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      <Icon className="h-4 w-4 mr-3 flex-shrink-0" />
      {item.label}
    </Link>
  );
}

// ─── Main Sidebar ────────────────────────────────────────────────────────────
export default function Sidebar({ isMobileSidebarOpen, setIsMobileSidebarOpen }) {
  const { logoutUser, hasRole, user } = useContext(AuthContext);
  const [isCustomDropdownOpen, setIsCustomDropdownOpen] = useState(false);
  const [isAuditHistoryDropdownOpen, setIsAuditHistoryDropdownOpen] = useState(false);
  const location = useLocation();

  // ── Access helper ──────────────────────────────────────────────────────────
  const canAccessItem = (requiredRoles) => requiredRoles.some(role => hasRole(role));

  // ── Nav item groups ────────────────────────────────────────────────────────
  const baseDashboardItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard',
      requiredRoles: ["Admin", "RiskManager", "RiskIdentifier", "CartoViewer", "AuditManager", "AuditUser"]
    },
    {
      id: 'myAssignedTask',
      label: 'Mes Tâches',
      icon: Map,
      path: '/dashboard/tasks/my-tasks',
      requiredRoles: ["Admin", "RiskManager", "RiskIdentifier", "CartoViewer", "AuditManager", "AuditUser"]
    },
    {
      id: 'my-recommendation-actions',
      label: 'Mes recommandations',
      icon: Target,
      path: '/dashboard/audit/my-recommendations',
      requiredRoles: ["Admin", "RiskManager", "RiskIdentifier", "CartoViewer", "AuditManager", "AuditUser"]
    },
  ];

  const riskManagementItems = [
    {
      id: 'risks',
      label: 'Gérer les risques',
      icon: AlertTriangle,
      path: '/dashboard/risks',
      requiredRoles: ["RiskManager", "RiskIdentifier"]
    },
    {
      id: 'allRisks',
      label: 'Risques',
      icon: AlertTriangle,
      path: '/dashboard/risks-organisation-all',
      requiredRoles: ["Admin"]
    },
    {
      id: 'activities',
      label: 'Gérer les activités',
      icon: Map,
      path: '/dashboard/activities',
      requiredRoles: ["RiskManager"]
    },
    {
      id: 'allActivities',
      label: 'Activités',
      icon: Map,
      path: '/dashboard/activities/all',
      requiredRoles: ["Admin"]
    },
    {
      id: 'cartographies',
      label: 'Gérer les cartographies',
      icon: Map,
      path: '/dashboard/cartographies',
      requiredRoles: ["RiskManager"]
    },
    {
      id: 'allCartographs',
      label: 'Cartographies',
      icon: Map,
      path: '/dashboard/cartographies/all-cartographies',
      requiredRoles: ["Admin"]
    },
    {
      id: 'publishCartographies',
      label: 'Cartographies Publier',
      icon: Eye,
      path: '/dashboard/cartographies/view',
      requiredRoles: ["RiskIdentifier", "CartoViewer"]
    },
    {
      id: 'allTasks',
      label: 'Toutes les Tâches',
      icon: Map,
      path: '/dashboard/tasks/all',
      requiredRoles: ["Admin", "RiskManager"]
    },
    {
      id: 'organization-infos',
      label: 'Organization',
      icon: Map,
      path: '/dashboard/admin/organization-infos',
      requiredRoles: ["Admin"]
    }
  ];

  const auditManagementItems = [
    {
      id: 'auditPrograms',
      label: "Programmes d'audit",
      icon: Target,
      path: '/dashboard/audit/programs',
      requiredRoles: ["AuditManager"]
    },
    {
      id: 'auditPlans',
      label: "Plan d'audit",
      icon: Map,
      path: '/dashboard/audit/plans',
      requiredRoles: ["AuditManager"]
    },
    {
      id: 'myAuditPlans',
      label: "Mes plans d'audit",
      icon: Target,
      path: '/dashboard/audit/my-plans',
      requiredRoles: ["AuditUser", "AuditManager"]
    },
    {
      id: 'publishedAudits',
      label: 'Audits publiés',
      icon: FileText,
      path: '/dashboard/audit/published-plans',
      requiredRoles: ["AuditManager", "AuditUser"]
    },
    {
      id: 'auditProgramsAdmin',
      label: "Programmes d'audit",
      icon: Target,
      path: '/dashboard/admin/audits/programs',
      requiredRoles: ["Admin"]
    },
    {
      id: 'auditPlansAdmin',
      label: "Plans d'audit",
      icon: FileText,
      path: '/dashboard/admin/audits/plans',
      requiredRoles: ["Admin"]
    },
    {
      id: 'recommendationActionsAdmin',
      label: 'Actions de recommandation',
      icon: CheckCircle,
      path: '/dashboard/admin/audits/recommendation-actions',
      requiredRoles: ["Admin"]
    },
  ];

  // ── Dropdown items ─────────────────────────────────────────────────────────
  const customDropdownItems = [
    { id: 'users',           label: 'Gérer les utilisateurs',    path: '/dashboard/admin/users',                requiredRoles: ["Admin"] },
    { id: 'userRoles',       label: 'Gérer les rôles',           path: '/dashboard/admin/user-roles',           requiredRoles: ["Admin"] },
    { id: 'auditUsers',      label: "Gérer users d'audit",       path: '/dashboard/admin/audit-users',          requiredRoles: ["Admin"] },
    { id: 'userLogs',        label: 'Journaux des utilisateurs', path: '/dashboard/admin/logs/users',           requiredRoles: ["Admin"] },
    { id: 'riskLogs',        label: 'Journaux des risques',      path: '/dashboard/admin/logs/risks',           requiredRoles: ["Admin"] },
    { id: 'activities',      label: 'Journaux des activités',    path: '/dashboard/admin/logs/activities',      requiredRoles: ["Admin"] },
    { id: 'processLogs',     label: 'Journaux des processus',    path: '/dashboard/admin/logs/processes',       requiredRoles: ["Admin"] },
    { id: 'processTaskLogs', label: 'Journaux des taches',       path: '/dashboard/admin/logs/process-tasks',  requiredRoles: ["Admin"] },
  ];

  const auditHistoryDropdownItems = [
    { id: 'auditProgramHistory',        label: 'Audit Program History',         path: '/dashboard/audit/history/programs',        requiredRoles: ["AuditManager"] },
    { id: 'auditPlanHistory',           label: 'Audit Plan History',            path: '/dashboard/audit/history/plans',           requiredRoles: ["AuditManager", "AuditUser"] },
    { id: 'auditSectionHistory',        label: 'Audit Section History',         path: '/dashboard/audit/history/sections',        requiredRoles: ["AuditManager", "AuditUser"] },
    { id: 'auditQuestionHistory',       label: 'Questions History',             path: '/dashboard/audit/history/questions',       requiredRoles: ["AuditManager", "AuditUser"] },
    { id: 'recommendationActionHistory',label: 'Recommendation Action History', path: '/dashboard/audit/history/recommendations', requiredRoles: ["AuditManager", "AuditUser"] },
    { id: 'followupHistory',            label: 'Follow-up History',             path: '/dashboard/audit/history/followups',       requiredRoles: ["AuditManager", "AuditUser"] },
  ];

  // ── Filtered lists based on user roles ────────────────────────────────────
  const visibleBaseItems         = baseDashboardItems.filter(item => canAccessItem(item.requiredRoles));
  const visibleRiskItems         = riskManagementItems.filter(item => canAccessItem(item.requiredRoles));
  const visibleAuditItems        = auditManagementItems.filter(item => canAccessItem(item.requiredRoles));
  const visibleDropdownItems     = customDropdownItems.filter(item => canAccessItem(item.requiredRoles));
  const visibleAuditHistoryItems = auditHistoryDropdownItems.filter(item => canAccessItem(item.requiredRoles));

  const showAdminDropdown        = hasRole("Admin");
  const showAuditHistoryDropdown = hasRole("AuditManager") || hasRole("AuditUser");

  // ── Helpers ────────────────────────────────────────────────────────────────
  const handleLinkClick = () => setIsMobileSidebarOpen(false);

  const getRoleDisplayName = (role) => role || 'User';

  const isPathActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';

    if (path === '/dashboard/cartographies') {
      return (
        location.pathname === '/dashboard/cartographies' ||
        (location.pathname.startsWith('/dashboard/cartographies/') &&
          !location.pathname.startsWith('/dashboard/cartographies/view'))
      );
    }

    if (path === '/dashboard/cartographies/view') {
      return location.pathname.startsWith('/dashboard/cartographies/view');
    }

    return location.pathname.startsWith(path);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col transform transition-transform duration-300 ease-in-out ${
        isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
        <div className="text-lg font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
          Cartopousse
        </div>
        <button
          onClick={() => setIsMobileSidebarOpen(false)}
          className="lg:hidden text-gray-500 hover:text-gray-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* ── Navigation ── */}
      <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-1">

        {/* General section — always visible */}
        {visibleBaseItems.length > 0 && (
          <>
            <SectionHeader label="Général" />
            {visibleBaseItems.map(item => (
              <NavItem
                key={item.id}
                item={item}
                isActive={isPathActive(item.path)}
                onClick={handleLinkClick}
              />
            ))}
          </>
        )}

        {/* Risk Management section */}
        {visibleRiskItems.length > 0 && (
          <>
            <SectionHeader label="Gestion des risques" />
            {visibleRiskItems.map(item => (
              <NavItem
                key={item.id}
                item={item}
                isActive={isPathActive(item.path)}
                onClick={handleLinkClick}
              />
            ))}
          </>
        )}

        {/* Audit Management section */}
        {visibleAuditItems.length > 0 && (
          <>
            <SectionHeader label="Gestion des audits" />
            {visibleAuditItems.map(item => (
              <NavItem
                key={item.id}
                item={item}
                isActive={isPathActive(item.path)}
                onClick={handleLinkClick}
              />
            ))}
          </>
        )}

        {/* Admin dropdown */}
        {showAdminDropdown && (
          <div className="relative">
            <SectionHeader label="Administration" />
            <button
              onClick={() => setIsCustomDropdownOpen(!isCustomDropdownOpen)}
              className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                isCustomDropdownOpen || visibleDropdownItems.some(item => isPathActive(item.path))
                  ? 'bg-gray-50 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center">
                <Settings className="h-4 w-4 mr-3" />
                Paramètres admin
              </div>
              {isCustomDropdownOpen
                ? <ChevronDown className="h-4 w-4" />
                : <ChevronRight className="h-4 w-4" />}
            </button>

            {isCustomDropdownOpen && (
              <div className="mt-1 ml-4 space-y-1">
                {visibleDropdownItems.map(item => {
                  const isActive = isPathActive(item.path);
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      onClick={handleLinkClick}
                      className={`w-full block text-left px-3 py-2 text-sm rounded-md transition-colors duration-200 ${
                        isActive
                          ? 'bg-green-50 text-green-700'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Audit History dropdown */}
        {showAuditHistoryDropdown && (
          <div className="relative">
            <SectionHeader label="Historique des audits" />
            <button
              onClick={() => setIsAuditHistoryDropdownOpen(!isAuditHistoryDropdownOpen)}
              className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                isAuditHistoryDropdownOpen || visibleAuditHistoryItems.some(item => isPathActive(item.path))
                  ? 'bg-gray-50 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center">
                <History className="h-4 w-4 mr-3" />
                Audit History
              </div>
              {isAuditHistoryDropdownOpen
                ? <ChevronDown className="h-4 w-4" />
                : <ChevronRight className="h-4 w-4" />}
            </button>

            {isAuditHistoryDropdownOpen && (
              <div className="mt-1 ml-4 space-y-1">
                {visibleAuditHistoryItems.map(item => {
                  const isActive = isPathActive(item.path);
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      onClick={handleLinkClick}
                      className={`w-full block text-left px-3 py-2 text-sm rounded-md transition-colors duration-200 ${
                        isActive
                          ? 'bg-green-50 text-green-700'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* ── User section ── */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center space-x-3 mb-3">
          <div className="w-8 h-8 bg-gradient-to-r from-black to-gray-900 rounded-full flex items-center justify-center">
            <User className="h-4 w-4 text-green-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{getRoleDisplayName(user?.role?.name)}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={() => logoutUser()}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Déconnexion
        </button>
      </div>
    </div>
  );
}